/**
 * email-capture.js
 *
 * Handles both Klaviyo-backed signup forms — the footer newsletter and the
 * password page's early-access capture. Which list a form subscribes to comes
 * from `data-list` on its wrapper, so the two share one implementation.
 *
 * Every form is a real Shopify `customer` form underneath. If this module never
 * loads, or Klaviyo errors, the native submit runs and the address lands in
 * Shopify's customer list instead. An email is never silently dropped.
 */

const config = window.AfterthreadKlaviyo;
const ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';

const PENDING_BIRTHDAY_KEY = 'afterthread:pending-birthday-signup';

function rememberBirthdaySignup(email, firstName) {
  try {
    sessionStorage.setItem(PENDING_BIRTHDAY_KEY, JSON.stringify({ email, firstName, createdAt: Date.now() }));
  } catch {}
}

function pendingBirthdaySignup() {
  try {
    const value = JSON.parse(sessionStorage.getItem(PENDING_BIRTHDAY_KEY));
    if (value && typeof value.email === 'string' && value.email.includes('@') && Date.now() - value.createdAt < 30 * 60 * 1000) return value;
    sessionStorage.removeItem(PENDING_BIRTHDAY_KEY);
  } catch {}
  return null;
}

function clearBirthdaySignup() {
  try { sessionStorage.removeItem(PENDING_BIRTHDAY_KEY); } catch {}
}

function listFor(name) {
  if (!config) return null;
  return name === 'early-access' ? config.earlyAccessList : config.newsletterList;
}

async function subscribe(email, listId, firstName) {
  const attributes = { email };
  if (firstName) attributes.first_name = firstName;

  const response = await fetch(`${ENDPOINT}?company_id=${encodeURIComponent(config.companyId)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/vnd.api+json',
      revision: '2026-07-15'
    },
    body: JSON.stringify({
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    })
  });

  if (!response.ok) throw new Error(`Klaviyo responded ${response.status}`);
}

/**
 * Files the same signup in Shopify, so an address exists in both systems.
 *
 * Klaviyo's Shopify integration syncs customers *out of* Shopify, not back
 * into it — so without this a Klaviyo subscriber would never appear in your
 * Shopify customer list.
 *
 * Deliberately non-fatal: by the time this runs Klaviyo already has the
 * address, so a failure here must not surface as a failed signup.
 */
async function alsoSaveToShopify(form) {
  try {
    const body = new URLSearchParams(new FormData(form));
    await fetch(form.action || '/contact', {
      method: 'POST',
      body,
      headers: { Accept: 'text/html' }
    });
  } catch (error) {
    /* Klaviyo has it; Shopify can be reconciled later. */
  }
}

function showSuccess(wrapper, form, email) {
  if (wrapper.closest('[data-newsletter-popup]') && wrapper.dataset.birthdayEnabled === 'true' && email) {
    clearBirthdaySignup();
    showBirthday(wrapper, form, email);
    return;
  }
  const message = document.createElement('p');
  message.className = wrapper.dataset.successClass || 'at-newsletter__success';
  message.setAttribute('role', 'status');
  message.textContent = wrapper.dataset.successMessage || '';
  form.replaceChildren(message);
}

function showBirthday(wrapper, signupForm, email) {
  const popup = wrapper.closest('[data-newsletter-popup]');
  const birthdayForm = document.createElement('form');
  birthdayForm.dataset.captureReady = 'true';
  const title = document.createElement('h3');
  title.textContent = wrapper.dataset.birthdayTitle;
  title.style.cssText = 'margin:0 0 12px;font-size:22px;line-height:1.25;font-weight:500;color:inherit';
  title.tabIndex = -1;
  const intro = document.createElement('p');
  intro.textContent = wrapper.dataset.birthdayBody;
  intro.style.cssText = 'margin:0 0 24px;font-size:14px;line-height:1.5';
  let emailInput = null;
  if (!email) {
    emailInput = document.createElement('input');
    emailInput.type = 'email';
    emailInput.required = true;
    emailInput.autocomplete = 'email';
    emailInput.className = 'at-popup__field';
    emailInput.placeholder = wrapper.dataset.birthdayEmailLabel;
    emailInput.setAttribute('aria-label', wrapper.dataset.birthdayEmailLabel);
  }
  const fields = document.createElement('div');
  fields.style.cssText = 'display:grid;grid-template-columns:1fr 1.4fr;gap:12px;text-align:left';
  function field(name, count, labelText) {
    const label = document.createElement('label');
    label.style.cssText = 'display:block;font-size:12px;line-height:1.5';
    const caption = document.createElement('span');
    caption.textContent = labelText;
    const select = document.createElement('select');
    select.name = name;
    select.required = true;
    select.className = 'at-popup__field';
    select.style.cssText = 'margin-top:6px;appearance:auto;text-align:left';
    select.append(new Option(labelText, ''));
    for (let i = 1; i <= count; i++) {
      const text = name === 'month'
        ? new Intl.DateTimeFormat(document.documentElement.lang || 'en', { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, i - 1, 1)))
        : String(i);
      select.append(new Option(text, String(i)));
    }
    label.append(caption, select);
    fields.append(label);
    return select;
  }
  const day = field('day', 31, wrapper.dataset.birthdayDay);
  const month = field('month', 12, wrapper.dataset.birthdayMonth);
  function syncDays() {
    const maximum = month.value ? new Date(Date.UTC(2000, Number(month.value), 0)).getUTCDate() : 31;
    for (const option of day.options) {
      option.disabled = Number(option.value) > maximum;
    }
    if (Number(day.value) > maximum) day.value = '';
  }
  month.addEventListener('change', syncDays);
  const save = document.createElement('button');
  save.type = 'submit';
  save.className = 'at-popup__submit';
  save.textContent = wrapper.dataset.birthdaySave;
  const skip = document.createElement('button');
  skip.type = 'button';
  skip.textContent = wrapper.dataset.birthdaySkip;
  skip.style.cssText = 'display:block;margin:12px auto 0;min-height:44px;padding:8px 16px;color:inherit;text-decoration:underline;font-size:12px;cursor:pointer';
  skip.addEventListener('click', () => popup.querySelector('button[data-newsletter-popup-close]')?.click());
  const error = document.createElement('p');
  error.className = 'at-popup__error';
  error.setAttribute('role', 'alert');
  error.hidden = true;
  birthdayForm.append(title, intro);
  if (emailInput) birthdayForm.append(emailInput);
  birthdayForm.append(fields, save, skip, error);
  popup.querySelector('.at-popup__body')?.setAttribute('hidden', '');
  popup.querySelector('.at-popup__fine')?.setAttribute('hidden', '');
  signupForm.replaceWith(birthdayForm);
  title.focus();
  birthdayForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (save.disabled) return;
    syncDays();
    if (!birthdayForm.reportValidity()) return;
    if (emailInput) email = emailInput.value.trim();
    save.disabled = true;
    day.disabled = true;
    month.disabled = true;
    error.hidden = true;
    birthdayForm.setAttribute('aria-busy', 'true');
    const birthday = '2000-' + month.value.padStart(2, '0') + '-' + day.value.padStart(2, '0');
    try {
      const response = await fetch('https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(config.companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' },
        body: JSON.stringify({data:{type:'profile',attributes:{email,properties:{Birthday:birthday,BirthdayMonth:Number(month.value),BirthdayDay:Number(day.value)}}}}),
        signal: AbortSignal.timeout(15000)
      });
      if (!response.ok) throw new Error('Birthday save failed');
      const confirmation = document.createElement('p');
      confirmation.className = 'at-popup__success';
      confirmation.setAttribute('role', 'status');
      confirmation.textContent = wrapper.dataset.birthdaySaved;
      confirmation.tabIndex = -1;
      birthdayForm.replaceChildren(confirmation, skip);
      confirmation.focus();
    } catch (failure) {
      error.textContent = wrapper.dataset.birthdayError;
      error.hidden = false;
      save.disabled = false;
      day.disabled = false;
      month.disabled = false;
    } finally {
      birthdayForm.removeAttribute('aria-busy');
    }
  });
}

function init() {
  if (!config || !config.companyId) return;

  document.querySelectorAll('[data-newsletter]').forEach((wrapper) => {
    const form = wrapper.querySelector('form');
    if (!form || form.dataset.captureReady) return;

    if (wrapper.closest('[data-newsletter-popup]') && wrapper.dataset.birthdayEnabled === 'true' && form.querySelector('[role="status"]')) {
      const pending = pendingBirthdaySignup();
      form.dataset.captureReady = 'true';
      const confirmedEmail = form.querySelector('[data-subscribed-email]')?.dataset.subscribedEmail;
      clearBirthdaySignup();
      showBirthday(wrapper, form, pending?.email || confirmedEmail || '');
      return;
    }

    const listId = listFor(wrapper.dataset.list);
    if (!listId) return; // No list configured — leave the native form alone.

    form.dataset.captureReady = 'true';

    form.addEventListener('submit', async (event) => {
      const input = form.querySelector('input[type="email"]');
      if (!input || !input.value.includes('@')) return;

      event.preventDefault();

      const submit = form.querySelector('button[type="submit"]');
      if (submit) submit.disabled = true;

      const email = input.value.trim();
      const firstName = form.querySelector('input[name="contact[first_name]"]')?.value.trim();
      if (wrapper.closest('[data-newsletter-popup]') && wrapper.dataset.birthdayEnabled === 'true') {
        rememberBirthdaySignup(email, firstName);
      }

      try {
        await subscribe(email, listId, firstName);
        // Klaviyo first — it is the destination that matters most here. Only
        // once it has succeeded do we mirror the signup into Shopify.
        await alsoSaveToShopify(form);
        showSuccess(wrapper, form, email);
      } catch (error) {
        // Hand back to Shopify's native submit rather than losing the address.
        if (submit) submit.disabled = false;
        form.dataset.captureReady = '';
        form.submit();
      }
    });
  });
}

init();
document.addEventListener('shopify:section:load', init);
